import { type Category, NO_CATEGORY_FILTER } from '@klopotun/contracts';
import { toCivilDate } from '@klopotun/domain';
import { useNavigate } from 'react-router';
import { useCategoriesQuery } from '../../entities/category/index.js';
import { useMeQuery } from '../../entities/me/index.js';
import {
  type CategorySpending,
  useMonthInsightQuery,
  useSummaryQuery,
  type YearSummary,
} from '../../entities/summary/index.js';
import { ROUTE, summaryCategoryPath } from '../../features/navigation/index.js';
import {
  byLargestSpending,
  categoryTitleOf,
  formatBaseMoney,
  formatMoneyMaybeApproximate,
  formatShare,
  LazySpendingChart,
  spentMinorOf,
  toChosenPeriod,
  toSpendingBreakdown,
  useChosenPeriod,
} from '../../features/summary-view/index.js';
import { List, ListItem, PageHeader, Screen } from '../../shared/ui/index.js';
import {
  CurrentMonthInsight,
  CurrentMonthLine,
  isSpendingApproximate,
  PeriodControls,
  SpendingMonths,
  SummaryFailureScreen,
  SummaryLoadingScreen,
  SummaryNotes,
  WithoutForecast,
  YearFigures,
} from '../../widgets/year-summary/index.js';
import styles from './summary-page.module.css';

const categoryKeyOf = (row: CategorySpending): string => row.categoryId ?? NO_CATEGORY_FILTER;

const shareOf = (row: CategorySpending, totalMinor: number): string | undefined => {
  const hasShare = spentMinorOf(row) > 0 && totalMinor > 0;

  return hasShare ? formatShare(spentMinorOf(row) / totalMinor) : undefined;
};

const isRowApproximate = (summary: YearSummary, row: CategorySpending): boolean => {
  const hasEstimates = row.estimatedMinor > 0;
  const hasConvertedPayment = summary.payments.some(
    (payment) => payment.isConverted && payment.categoryId === row.categoryId,
  );

  return hasEstimates || hasConvertedPayment;
};

const chartLabelOf = (month: number | null): string =>
  month === null ? 'Витрати за рік по категоріях' : 'Витрати за місяць по категоріях';

export const SummaryPage = () => {
  const navigate = useNavigate();
  const { chosen, choose } = useChosenPeriod();
  const me = useMeQuery();
  const summary = useSummaryQuery(chosen.year, chosen.month);
  const isCurrentYearChosen = chosen.year === null;
  const insight = useMonthInsightQuery(isCurrentYearChosen);
  const categories = useCategoriesQuery({ includeArchived: true });
  const isInsightLoading = isCurrentYearChosen && insight.isPending;

  if (me.isPending || summary.isPending || isInsightLoading) {
    return <SummaryLoadingScreen />;
  }

  if (me.isError) {
    return (
      <SummaryFailureScreen
        message={me.error.message}
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  if (summary.isError) {
    return (
      <SummaryFailureScreen
        message={summary.error.message}
        onRetry={() => {
          void summary.refetch();
        }}
      />
    );
  }

  const loaded = summary.data;
  const currency = loaded.baseCurrency;
  const categoryById = new Map(
    (categories.data ?? []).map((category) => [category.id, category] as const),
  );
  const categoryOf = (row: CategorySpending): Category | undefined =>
    row.categoryId === null ? undefined : categoryById.get(row.categoryId);
  const today = toCivilDate(new Date(), me.data.settings.timezone);
  const breakdown = toSpendingBreakdown(
    loaded.categories.map((row) => ({
      key: categoryKeyOf(row),
      label: categoryTitleOf(categoryOf(row)),
      color: categoryOf(row)?.color ?? null,
      exactMinor: row.exactMinor,
      estimatedMinor: row.estimatedMinor,
    })),
  );
  const categoryRows = loaded.categories.toSorted(byLargestSpending);
  const isPastYear = loaded.year < today.year;
  const isWaitingForSubscriptions = loaded.bank === null && !isPastYear;
  const emptyCategoriesText = isWaitingForSubscriptions ? 'Ще немає підписок' : 'Ще немає витрат';
  const insightOfYear = insight.data?.year === loaded.year ? insight.data : undefined;
  const monthInsight = insightOfYear?.month === loaded.month ? insightOfYear : undefined;
  const yearInsight = loaded.month === null ? insightOfYear : undefined;

  return (
    <Screen>
      <div className={styles.content}>
        <PageHeader title="Витрати" />
        <PeriodControls
          shown={{ year: loaded.year, month: loaded.month }}
          today={today}
          onChoose={choose}
        />

        {monthInsight !== undefined && (
          <CurrentMonthInsight
            insight={monthInsight}
            onOpenCategory={(categoryId) => {
              void navigate(summaryCategoryPath(categoryId, chosen));
            }}
          />
        )}
        {yearInsight !== undefined && (
          <CurrentMonthLine
            insight={yearInsight}
            onOpen={() => {
              choose(
                toChosenPeriod({ year: yearInsight.year, month: yearInsight.month }, today.year),
              );
            }}
          />
        )}

        <YearFigures summary={loaded} isOverview />
        <SummaryNotes summary={loaded} />

        {breakdown.slices.length > 0 && (
          <LazySpendingChart
            slices={breakdown.slices}
            totalMinor={breakdown.totalMinor}
            currency={currency}
            isApproximate={isSpendingApproximate(loaded)}
            label={chartLabelOf(loaded.month)}
          />
        )}

        <List header="По категоріях">
          {categoryRows.length === 0 && <ListItem title={emptyCategoriesText} />}
          {categoryRows.map((row) => (
            <ListItem
              key={categoryKeyOf(row)}
              title={categoryTitleOf(categoryOf(row))}
              marker={breakdown.colorByKey.get(categoryKeyOf(row))}
              subtitle={shareOf(row, breakdown.totalMinor)}
              value={formatMoneyMaybeApproximate(
                spentMinorOf(row),
                currency,
                isRowApproximate(loaded, row),
              )}
              valueNote={
                row.annualCostMinor === 0
                  ? undefined
                  : `${formatBaseMoney(row.annualCostMinor, currency)} на рік`
              }
              chevron
              onClick={() => {
                void navigate(summaryCategoryPath(categoryKeyOf(row), chosen));
              }}
            />
          ))}
        </List>

        <SpendingMonths
          summary={loaded}
          onChooseMonth={(month) => {
            choose(toChosenPeriod({ year: loaded.year, month }, today.year));
          }}
        />
        <WithoutForecast summary={loaded} />

        <List>
          <ListItem
            title="Календар навантаження"
            subtitle="Що і коли списується, по днях"
            chevron
            onClick={() => {
              void navigate(ROUTE.calendar, { replace: true });
            }}
          />
        </List>
      </div>
    </Screen>
  );
};
